import React, { useState, useEffect, ReactElement } from 'react';
import { useForm, SubmitHandler, useFieldArray, useWatch, FieldError } from 'react-hook-form';
import { useFetch } from 'hooks/use-fetch';
import Button from 'atomic/button';
import Icon from 'atomic/icon';
import Checkbox from 'atomic/form/checkbox';
import Input from 'atomic/form/input';
import Accordion from 'modules/shared/components/accordion';
import NewsletterResponseSuccess from 'components/newsletter/newsletter-response-success';
import { FreshmailNewsletterInterface } from 'modules/freshmail/freshmail-newsletter.interface';
import { FreshmailResponseInterface } from 'modules/freshmail/freshmail-response.interface';
import { newsletterFormData } from 'components/newsletter/newsletter-form/newsletter-form.data';

import './newsletter-form.scss';

interface NewsletterFormProps {
    isMobile: boolean;
    onFormSubmit?: () => void;
}

const NewsletterForm: React.FC<NewsletterFormProps> = ({ isMobile, onFormSubmit }) => {
    const [isLoadingNewsletter, setIsLoadingNewsletter] = useState(false);
    const [isResponseSuccess, setIsResponseSuccess] = useState(false);
    const [isOpenAccordion, setIsOpenAccordion] = useState(true);
    const { post } = useFetch('/newsletter/signup');
    const {
        register,
        control,
        handleSubmit,
        setError,
        clearErrors,
        formState: { errors }
    } = useForm<FreshmailNewsletterInterface>({
        mode: 'onChange',
        defaultValues: {
            categories: newsletterFormData.list.map(item => ({
                ...item,
                value: true
            }))
        }
    });

    const { fields } = useFieldArray({
        control,
        name: 'categories'
    });

    const categories = useWatch({
        control,
        name: 'categories'
    });

    useEffect(() => {
        const isCategoriesEmpty = categories.every(item => !item.value);

        if (isCategoriesEmpty) {
            setError('categories', { type: 'required' });
        } else {
            clearErrors('categories');
        }
    }, [categories, setError, clearErrors]);

    const onSubmit: SubmitHandler<FreshmailNewsletterInterface> = (data: FreshmailNewsletterInterface) => {
        setIsLoadingNewsletter(true);
        post(data).then((res: FreshmailResponseInterface) => {
            if (res.status === 'OK') {
                setIsResponseSuccess(true);

                if (onFormSubmit) {
                    onFormSubmit();
                }
            } else {
                setIsLoadingNewsletter(false);
                setError('email', {
                    type: 'error',
                    message: res.error
                });
            }
        });
    };

    const renderText = (isOpen: boolean): ReactElement => {
        return (
            <p className="newsletter-form__text">
                {isOpen ? newsletterFormData.text : newsletterFormData.message}
            </p>
        );
    };

    const renderList = (): ReactElement => {
        return (
            <ul className="newsletter-form__list">
                {fields.map((item, index) => (
                    <li
                        key={item.id}
                        className="newsletter-form__item"
                    >
                        <Checkbox
                            {...register(`categories.${index}.value`)}
                            id={item.id}
                            value={item.name}
                            label={item.label}
                            disabled={isLoadingNewsletter}
                            error={errors.categories as FieldError}
                        />
                    </li>
                ))}
            </ul>
        );
    };

    const renderAccordion = (): ReactElement => {
        if (!isMobile) {
            return (
                <>
                    {renderText(true)}
                    {renderList()}
                </>
            );
        }

        return (
            <Accordion
                index={0}
                activeTab={isOpenAccordion ? 0 : null}
                handleToggle={(): void => setIsOpenAccordion((state: boolean): boolean => !state)}
                contentContainerTagName="div"
                label={renderText(isOpenAccordion)}
            >
                {renderList()}
            </Accordion>
        );
    };

    if (isResponseSuccess) {
        return (
            <NewsletterResponseSuccess />
        );
    }

    return (
        <form
            className="newsletter-form"
            onSubmit={handleSubmit(onSubmit)}
        >
            <div className="newsletter-form__container">
                <Input
                    {...register(
                        'email',
                        {
                            required: newsletterFormData.errors.requiredMessage,
                            pattern: {
                                value: /\S+@\S+\.\S+/,
                                message: newsletterFormData.errors.patternMessage
                            }
                        }
                    )}
                    type="email"
                    name="email"
                    label="Podaj swój adres e-mail"
                    error={errors.email}
                    disabled={isLoadingNewsletter}
                />
                <div className="newsletter-form__wrapper">
                    {renderAccordion()}
                </div>
                <Button
                    type="submit"
                    color="primary"
                    block={true}
                    disabled={isLoadingNewsletter}
                >
                    <Icon
                        name="mail"
                        size={24}
                        color="white"
                    />
                    Zapisz się
                </Button>
            </div>
        </form>
    );
};

export default NewsletterForm;
